/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useWavesurfer } from '@wavesurfer/react';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import { useDispatch, useSelector } from 'react-redux';
import { selectRecordingDuration, selectReportId, setRecordingDuration, setRecordingStarted, setReportId } from '@/store/slices/ReportsSlice';
import { createRecordingLog } from '@/api/imperative/recording'
import { v4 as uuidv4 } from "uuid";
import { isIOS, isMacOs, isSafari } from 'react-device-detect';
import { toast } from 'react-toastify';
import { withWebmDuration } from '@/shared/audio/withWebmDuration';
import { MIN_AUDIBLE_MS } from '@/shared/audio/isAudioSilent';
import RecorderControls from '@/features/recording/RecorderControls';
import { useRecordingLevel } from '@/features/recording/useRecordingLevel';
import {
  AUDIO_BITS_PER_SECOND,
  FINALIZE_WATCHDOG_MS,
  MIN_BLOB_BYTES,
  buildFinalBlob,
  createSafeObjectURL,
  getPauseCount,
  incrementPauseCount,
  maxDuration,
  playbackDuration,
  resetPauseCount,
  revokeAllObjectURLs,
  revokeSafeObjectURL,
  timeout,
} from '@/features/recording/recordingSession';

const Recorder = ({
  handleAudio,
  setRecordingTime,
  autoStart = false,
  finalising = false,
  setFinalising = () => {},
  onAbandonReady,
  confirmIfSilent,
}) => {
  const containerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const firstChunks = useRef([]);
  const audioUrl = useRef(null);
  const restartStatusRef = useRef(false);
  const isProcessingRef = useRef(false);
  const allAudioChunksRef = useRef([]); // Store ALL chunks for final recording
  const waitingForFinalChunk = useRef(false);
  const finalChunkReceived = useRef(false);
  const recordEndTimeoutRef = useRef(null);
  const recordEndWaitingRef = useRef(false);
  const recordEndBlobRef = useRef(null);
  const isRecordingDoneRef = useRef(false);
  const finalizeCompletedRef = useRef(false);
  const finalizeWatchdogRef = useRef(null);
  const processRecordingEndRef = useRef(null);
  const onRecordEndRef = useRef(null);
  const onDataAvailableRef = useRef(null);
  const lastDispatchedSecondRef = useRef(-1);
  const recordedMsRef = useRef(0);
  const {
    heardMsRef,
    levelReadyRef,
    startLevelMonitor,
    stopLevelMonitor,
    syncRecordingLevel,
  } = useRecordingLevel();
  const [durationProgress, setDurationProgress] = useState(0);
  const reportId = useSelector(selectReportId);
  const [active, setActive] = useState(autoStart);
  const [startRecording, setStartRecording] = useState(autoStart);
  const [recordingDone, setRecordingDone] = useState(false);
  const duration = useSelector(selectRecordingDuration);
  const [playBackState, setPlayBackState] = useState('pause');
  const [isDisabled, setIsDisabled] = useState(true);
  const [playback, setPlayback] = useState(false);
  const [processingUrl, setProcessingUrl] = useState(false);
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    return () => {
      revokeAllObjectURLs();

      if (recordEndTimeoutRef.current) {
        clearTimeout(recordEndTimeoutRef.current);
        recordEndTimeoutRef.current = null;
      }
      if (finalizeWatchdogRef.current) {
        clearTimeout(finalizeWatchdogRef.current);
        finalizeWatchdogRef.current = null;
      }
    };
  }, []);

  const { wavesurfer } = useWavesurfer({
    container: containerRef,
    height: 60,
    waveColor: '#535A63',
    progressColor: '#EE772F',
    barHeight: 1,
    barWidth: 2,
    barRadius: 4,
    barGap: 4,
  });

  // Cleanup wavesurfer instance on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (wavesurfer) {
        try {
          wavesurfer.destroy();
        } catch (err) {
          console.error("Error destroying wavesurfer:", err);
        }
      }
    };
  }, [wavesurfer]);

  const recordPluginInstance = useMemo(() => {
    if (wavesurfer) {
      const pluginOptions = {
        scrollingWaveform: true,
        renderRecordedAudio: false,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        mediaRecorderTimeslice: 1000, // call onDataAvailable every second
        ...((isIOS || (isMacOs && isSafari)) && { mimeType: 'audio/mp4' })
      };

      return wavesurfer?.registerPlugin(RecordPlugin.create(pluginOptions));
    }
    return null
  }, [wavesurfer]);

  const isPaused = playback && recordPluginInstance && (recordPluginInstance.isPaused() || !active)

  useEffect(() => {
    resetPauseCount();
    allAudioChunksRef.current = []; // Initialize on mount
    isProcessingRef.current = false;
    waitingForFinalChunk.current = false;
    finalChunkReceived.current = false;
    finalizeCompletedRef.current = false;
    recordEndBlobRef.current = null;
    lastDispatchedSecondRef.current = -1;
    recordedMsRef.current = 0;
    heardMsRef.current = 0;
  }, [])

  const clearFinalizeWatchdog = useCallback(() => {
    if (finalizeWatchdogRef.current) {
      clearTimeout(finalizeWatchdogRef.current);
      finalizeWatchdogRef.current = null;
    }
  }, []);

  const failFinalize = useCallback((message) => {
    clearFinalizeWatchdog();
    isProcessingRef.current = false;
    isRecordingDoneRef.current = false;
    finalizeCompletedRef.current = false;
    waitingForFinalChunk.current = false;
    finalChunkReceived.current = false;
    recordEndWaitingRef.current = false;
    recordEndBlobRef.current = null;
    if (recordEndTimeoutRef.current) {
      clearTimeout(recordEndTimeoutRef.current);
      recordEndTimeoutRef.current = null;
    }
    setFinalising(false);
    stopLevelMonitor();
    setActive(false);
    setRecordingDone(false);
    setPlayback(false);
    setPlayBackState('pause');
    setProcessingUrl(false);
    toast.error(message);
  }, [setFinalising, clearFinalizeWatchdog]);

  const completeFinalize = useCallback((finalBlob) => {
    if (finalizeCompletedRef.current) return;
    finalizeCompletedRef.current = true;
    isProcessingRef.current = false;
    isRecordingDoneRef.current = false;
    waitingForFinalChunk.current = false;
    finalChunkReceived.current = true;
    recordEndWaitingRef.current = false;
    recordEndBlobRef.current = null;

    if (recordEndTimeoutRef.current) {
      clearTimeout(recordEndTimeoutRef.current);
      recordEndTimeoutRef.current = null;
    }
    clearFinalizeWatchdog();

    console.log("Final recording ready, passing to parent:", finalBlob.size, "bytes, type:", finalBlob.type);
    handleAudio(finalBlob);
  }, [handleAudio, clearFinalizeWatchdog]);

  const startFinalizeWatchdog = useCallback(() => {
    clearFinalizeWatchdog();
    finalizeWatchdogRef.current = setTimeout(() => {
      if (finalizeCompletedRef.current) return;
      console.warn("Finalize watchdog: forcing completion after", FINALIZE_WATCHDOG_MS, "ms");
      waitingForFinalChunk.current = false;
      finalChunkReceived.current = true;
      recordEndWaitingRef.current = false;
      if (recordEndTimeoutRef.current) {
        clearTimeout(recordEndTimeoutRef.current);
        recordEndTimeoutRef.current = null;
      }
      processRecordingEndRef.current?.();
    }, FINALIZE_WATCHDOG_MS);
  }, [clearFinalizeWatchdog]);

  const processRecordingEnd = useCallback(async () => {
    if (finalizeCompletedRef.current) return;

    try {
      if (recordEndTimeoutRef.current) {
        clearTimeout(recordEndTimeoutRef.current);
        recordEndTimeoutRef.current = null;
      }
      recordEndWaitingRef.current = false;

      const pluginBlob = recordEndBlobRef.current;
      console.log("Processing recording end, chunks:", allAudioChunksRef.current.length, "plugin blob:", pluginBlob?.size);

      const finalBlob = buildFinalBlob(allAudioChunksRef.current, pluginBlob);

      if (!finalBlob || finalBlob.size < MIN_BLOB_BYTES) {
        console.error("No valid audio data available");
        failFinalize("Recording failed - no audio data captured");
        return;
      }

      const blobToSave = await withWebmDuration(finalBlob, recordedMsRef.current);
      audioUrl.current = createSafeObjectURL(blobToSave);
      completeFinalize(blobToSave);
    } catch (error) {
      console.error("Error in processRecordingEnd:", error);
      failFinalize("Error processing recording: " + error.message);
    }
  }, [completeFinalize, failFinalize]);

  useEffect(() => {
    processRecordingEndRef.current = processRecordingEnd;
  }, [processRecordingEnd]);

  useEffect(() => {
    if (!recordPluginInstance || !startRecording) return;

    dispatch(setRecordingStarted(true));
    setStartRecording(false);
    setIsDisabled(true);

    recordEndBlobRef.current = null;
    lastDispatchedSecondRef.current = -1;
    recordedMsRef.current = 0;
    heardMsRef.current = 0;

    (async () => {
      try {
        const devices = await RecordPlugin.getAvailableAudioDevices();
        const deviceId = devices[0]?.deviceId;
        await recordPluginInstance.startRecording({ deviceId });
        const id = uuidv4();
        dispatch(setReportId(id));
        createRecordingLog({ type: 'start', duration: 0, reportId: id, createdAt: new Date().toISOString() });
        setActive(true);
        setPlayBackState('pause');
        setPlayback(false);
        startLevelMonitor(recordPluginInstance.stream);
      } catch (err) {
        console.error('Failed to start recording:', err);
        toast.error('Failed to start recording.');
      } finally {
        setTimeout(() => setIsDisabled(false), 1500);
      }
    })();
  }, [recordPluginInstance, startRecording, dispatch]);

  const onRecordEnd = useCallback(async (blob) => {
    try {
      console.log("onRecordEnd called, blob size:", blob?.size, "isRecordingDone:", isRecordingDoneRef.current, "total chunks:", allAudioChunksRef.current.length);

      if (!isRecordingDoneRef.current) {
        console.log("Recording ended but not marked as done, ignoring");
        return;
      }

      recordEndBlobRef.current = blob;

      if (waitingForFinalChunk.current && !finalChunkReceived.current) {
        console.log("Waiting for final chunk before processing...");
        recordEndWaitingRef.current = true;

        recordEndTimeoutRef.current = setTimeout(() => {
          console.warn("Final chunk timeout reached, processing with available chunks");
          finalChunkReceived.current = true;
          processRecordingEndRef.current?.();
        }, 2000);
        return;
      }

      processRecordingEnd();
    } catch (error) {
      console.error("Error in onRecordEnd:", error);
      failFinalize("Error processing recording: " + error.message);
    }
  }, [processRecordingEnd, failFinalize]);

  const onDataAvailable = useCallback(async (blob) => {
    if (!restartStatusRef.current) {
      // Validate chunk has data
      if (blob && blob.size > 0) {
        // Always store in allAudioChunksRef for final recording
        allAudioChunksRef.current.push(blob);
        
        // Store in audioChunksRef for playback (limited to last 60 seconds)
        audioChunksRef.current.push(blob);
        if (audioChunksRef.current.length <= 2) {
          firstChunks.current.push(blob); // store first 2 chunk, needed for decoding
        }
        while (audioChunksRef.current.length > playbackDuration + getPauseCount()) {
          audioChunksRef.current.shift();
        }

        // If we were waiting for the final chunk, signal it's received
        if (waitingForFinalChunk.current && !finalChunkReceived.current) {
          console.log("Final chunk received, triggering processing");
          finalChunkReceived.current = true;
          if (recordEndTimeoutRef.current) {
            clearTimeout(recordEndTimeoutRef.current);
            recordEndTimeoutRef.current = null;
          }
          // record-end may have fired first and scheduled a timeout; process now
          if (recordEndWaitingRef.current) {
            processRecordingEndRef.current?.();
          }
        }
      } else {
        console.warn("Received empty chunk in onDataAvailable");
      }
    } else {
      console.log("Skipping chunk due to restart status");
    }
  }, [processRecordingEnd]);
  
  const resetState = () => {
    console.log("resetState called");
    restartStatusRef.current = true;
    setActive(false);
    setPlayback(false);
    setProcessingUrl(false);
    audioChunksRef.current = [];
    firstChunks.current = [];
    allAudioChunksRef.current = []; // Clear all chunks on restart
    resetPauseCount();
    isProcessingRef.current = false;
    isRecordingDoneRef.current = false;
    finalizeCompletedRef.current = false;
    waitingForFinalChunk.current = false;
    finalChunkReceived.current = false;
    recordEndWaitingRef.current = false;
    recordEndBlobRef.current = null;
    lastDispatchedSecondRef.current = -1;
    recordedMsRef.current = 0;
    heardMsRef.current = 0;
    stopLevelMonitor();
    setIsDisabled(true);
    
    // Clear any pending timeouts
    if (recordEndTimeoutRef.current) {
      clearTimeout(recordEndTimeoutRef.current);
      recordEndTimeoutRef.current = null;
    }
    clearFinalizeWatchdog();

    setTimeout(() => {
      setIsDisabled(false);
    }, 1500);
  }

  const abandonRecording = useCallback(async () => {
    restartStatusRef.current = true;
    stopLevelMonitor();
    if (recordPluginInstance) {
      try {
        if (active) {
          recordPluginInstance.stopRecording();
        }
      } catch (err) {
        console.error('stopRecording failed during abandon:', err);
      }
    }
    createRecordingLog({ type: 'discard', duration, reportId, createdAt: new Date().toISOString() });
  }, [recordPluginInstance, active, duration, reportId]);

  useEffect(() => {
    onAbandonReady?.(abandonRecording);
    return () => onAbandonReady?.(null);
  }, [abandonRecording, onAbandonReady]);

  const onStartStop = async () => {
    if (recordPluginInstance) {
      if (active) {
        // Stop recording and reset state
        stopLevelMonitor();
        try {
          recordPluginInstance.stopRecording();
        } catch (err) {
          console.error("stopRecording failed during restart:", err);
        }
        createRecordingLog({ type:'discard', duration, reportId, createdAt: new Date().toISOString() });
        resetState()

        const devices = await RecordPlugin.getAvailableAudioDevices();
        const deviceId = devices[0]?.deviceId;

        await timeout(500);
        restartStatusRef.current = false;
        recordEndBlobRef.current = null;
        await recordPluginInstance.startRecording({ deviceId });
        createRecordingLog({ type:'restart', duration: 0, reportId, createdAt: new Date().toISOString() });
        startLevelMonitor(recordPluginInstance.stream);
        setActive(true);
        setPlayBackState('pause');
        setTimeout(() => {
          setIsDisabled(false);
        }, 1500);
      } else {
        const devices = await RecordPlugin.getAvailableAudioDevices();
        const deviceId = devices[0]?.deviceId;

        setIsDisabled(true);
        recordEndBlobRef.current = null;
        await recordPluginInstance.startRecording({ deviceId });
        startLevelMonitor(recordPluginInstance.stream);
        setActive(true);
        setPlayBackState('pause');
        setPlayback(false);

        setTimeout(() => {
          setIsDisabled(false);
        }, 1500);
      }
    }
  };

  useEffect(() => {
    setDurationProgress(duration);
  }, [duration]);
  
  const resumePausedRecording = useCallback(() => {
    isProcessingRef.current = false;
    setFinalising(false);
    setRecordingDone(false);
    try {
      if (recordPluginInstance?.isPaused()) {
        recordPluginInstance.resumeRecording();
        createRecordingLog({ type: 'resume', duration, reportId, createdAt: new Date().toISOString() });
      }
      setActive(true);
      setPlayBackState('pause');
      setPlayback(false);
    } catch (err) {
      console.error('Failed to resume recording after silent audio:', err);
      toast.error('Failed to resume recording.');
    }
  }, [recordPluginInstance, duration, reportId, setFinalising]);

  const processAudio = useCallback(async () => {
    if (isProcessingRef.current) {
      console.log("processAudio already in progress, ignoring duplicate call");
      return;
    }

    if (!recordPluginInstance) return;

    isProcessingRef.current = true;

    // Live count only skips the check when ≥1s was heard. Never use it to warn.
    if (confirmIfSilent && !(levelReadyRef.current && heardMsRef.current >= MIN_AUDIBLE_MS)) {
      setFinalising(true);
      let pausedForCheck = false;
      const releaseCheck = () => {
        if (pausedForCheck) resumePausedRecording();
        else {
          isProcessingRef.current = false;
          setFinalising(false);
        }
      };
      try {
        if (active && !recordPluginInstance.isPaused()) {
          recordPluginInstance.pauseRecording();
          pausedForCheck = true;
          await timeout(400);
        }
        const chunks = allAudioChunksRef.current;
        const checkBlob = chunks.length
          ? new Blob(chunks, { type: chunks[0]?.type || 'audio/webm' })
          : null;
        const proceed = checkBlob ? await confirmIfSilent(checkBlob) : true;
        if (!proceed) {
          releaseCheck();
          return;
        }
      } catch (err) {
        console.error('Silence check failed:', err);
        releaseCheck();
        return;
      }
    }

    console.log("processAudio called, duration:", duration, "chunks:", allAudioChunksRef.current.length);
    recordEndBlobRef.current = null;
    finalizeCompletedRef.current = false;
    setFinalising(true);
    isRecordingDoneRef.current = true;

    waitingForFinalChunk.current = true;
    finalChunkReceived.current = false;

    createRecordingLog({ type:'end', duration, reportId, createdAt: new Date().toISOString() });
    setRecordingTime(Math.floor(duration));
    setRecordingDone(true);

    startFinalizeWatchdog();
    stopLevelMonitor();
    try {
      recordPluginInstance.stopRecording();
    } catch (err) {
      console.error("stopRecording failed:", err);
      failFinalize(err?.message ? `Failed to stop recording: ${err.message}` : "Failed to stop recording");
    }
  }, [recordPluginInstance, duration, reportId, setRecordingTime, setFinalising, startFinalizeWatchdog, failFinalize, confirmIfSilent, active, resumePausedRecording]);

  // stop and process audio on 3 hours duration
  useEffect(() => {
    if (durationProgress >= maxDuration) {
      processAudio();
    }
  }, [durationProgress, maxDuration, processAudio]);

  const onPauseResume = () => {
    // console.log("D1: ", duration)
    if (recordPluginInstance) {
      if (recordPluginInstance.isPaused()) {
        createRecordingLog({ type:'resume', duration, reportId, createdAt: new Date().toISOString() });
        recordPluginInstance.resumeRecording();
        setPlayBackState('pause');
        setPlayback(false);

        setIsDisabled(true)
        setTimeout(() => {
          setIsDisabled(false)
        }, 1500)
        return;
      }
      handlePause();
    }
  };

  const handlePause = async () => {
    incrementPauseCount();
    console.log('pause count', getPauseCount(), 'total chunks:', allAudioChunksRef.current.length);
    setProcessingUrl(true);
    createRecordingLog({ type:'pause', duration, reportId, createdAt: new Date().toISOString() });
    recordPluginInstance.pauseRecording();
    await timeout(1000);

    let combinedBlob;
    try {
      const chunksCount = audioChunksRef.current.length;
      console.log('chunksCount:', chunksCount);
      
      // Validate we have audio chunks
      if (audioChunksRef.current.length === 0) {
        console.error("No audio chunks available for playback");
        setProcessingUrl(false);
        toast.error("No audio data captured");
        return;
      }

      if (chunksCount >= (playbackDuration + getPauseCount())) {
        const blob = new Blob(firstChunks.current, { type: firstChunks.current[0].type });
        audioChunksRef.current.unshift(blob);
      } 
      combinedBlob = new Blob(audioChunksRef.current, { type: audioChunksRef.current[0].type });
      console.log('combinedBlob:', combinedBlob.size, 'bytes');
      
      // Validate blob size
      if (combinedBlob.size < MIN_BLOB_BYTES) {
        console.error("Combined blob too small, likely no audio");
        setProcessingUrl(false);
        toast.error("Recording appears to be empty or silent");
        return;
      }

      // Do not decode stitched chunks (WebM/MP4) — WaveSurfer plays the blob URL directly

      if (audioUrl.current) {
        revokeSafeObjectURL(audioUrl.current);
      }
      const newAudioUrl = createSafeObjectURL(combinedBlob);
      audioUrl.current = newAudioUrl;
      setPlayback(true);
      setProcessingUrl(false);  
    } catch (err) {
      console.error("Error while processing pause:", err);
      setProcessingUrl(false);
      toast.error("Error creating playback: " + err.message);
    }
  };

  const handlePlaybackState = useCallback((state) => {
    setPlayBackState(state);
  }, []);

  const handlePlaybackPlayPause = () => {
    if (playBackState === 'pause') {
      setPlayBackState('resume');
      return;
    }
    setPlayBackState('pause');
  };

  useEffect(() => {
    onRecordEndRef.current = onRecordEnd;
  }, [onRecordEnd]);

  useEffect(() => {
    onDataAvailableRef.current = onDataAvailable;
  }, [onDataAvailable]);

  useEffect(() => {
    if (!recordPluginInstance) return;

    const handleRecordEnd = (blob) => onRecordEndRef.current?.(blob);
    const handleDataAvailable = (blob) => onDataAvailableRef.current?.(blob);
    const handleRecordProgress = (time) => {
      recordedMsRef.current = time;
      const wholeSeconds = Math.floor(time / 1000);
      if (wholeSeconds === lastDispatchedSecondRef.current) return;
      lastDispatchedSecondRef.current = wholeSeconds;
      dispatchRef.current(setRecordingDuration(wholeSeconds));
    };

    recordPluginInstance.on('record-end', handleRecordEnd);
    recordPluginInstance.on('record-data-available', handleDataAvailable);
    recordPluginInstance.on('record-progress', handleRecordProgress);

    return () => {
      try {
        recordPluginInstance.un('record-end', handleRecordEnd);
        recordPluginInstance.un('record-data-available', handleDataAvailable);
        recordPluginInstance.un('record-progress', handleRecordProgress);
      } catch (err) {
        console.warn("Error removing event listeners:", err);
      }
    };
  }, [recordPluginInstance]);

  syncRecordingLevel(recordPluginInstance);

  const showPlayback = isPaused || (playback && recordingDone);

  return (
    <RecorderControls
      containerRef={containerRef}
      playback={playback}
      recordPluginInstance={recordPluginInstance}
      active={active}
      duration={duration}
      showPlayback={showPlayback}
      playbackAudioUrl={audioUrl.current}
      playBackState={playBackState}
      onPlaybackStateChange={handlePlaybackState}
      playbackMuted={audioChunksRef.current.length > (playbackDuration + getPauseCount())}
      isPaused={isPaused}
      onStartStop={onStartStop}
      onPlaybackPlayPause={handlePlaybackPlayPause}
      onPauseResume={onPauseResume}
      isDisabled={isDisabled}
      processingUrl={processingUrl}
      onDone={processAudio}
      finalising={finalising}
      isProcessing={isProcessingRef.current}
    />
  );
};

export default Recorder;
